import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { getToken } from '../../services/authToken';
import { useLanguage } from '../../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Theme definitions for the 3 cabin career stages.
 * Mapping is order-based: order 1-4 → Foundations, 5-10 → Interview Prep, 11-16 → Sector.
 */
const THEMES = [
  {
    id: 'foundations',
    order: 1,
    name_tr: 'Temeller',
    name_en: 'Foundations',
    sub_tr: 'Modül 1-4 · Temel konular',
    sub_en: 'Modules 1-4 · Core concepts',
    theme_icon: '🎯',
    gradient: 'from-blue-500 to-indigo-600',
    shadowColor: '#6366F1',
    moduleOrderRange: [1, 4],
  },
  {
    id: 'interview',
    order: 2,
    name_tr: 'Mülakat Hazırlık',
    name_en: 'Interview Prep',
    sub_tr: 'Modül 5-10 · Hazırlık becerileri',
    sub_en: 'Modules 5-10 · Preparation skills',
    theme_icon: '💼',
    gradient: 'from-purple-500 to-pink-600',
    shadowColor: '#EC4899',
    moduleOrderRange: [5, 10],
  },
  {
    id: 'sector',
    order: 3,
    name_tr: 'Sektör & Uzmanlık',
    name_en: 'Sector & Expertise',
    sub_tr: 'Modül 11-16 · Sektör uzmanlığı',
    sub_en: 'Modules 11-16 · Industry expertise',
    theme_icon: '✈️',
    gradient: 'from-amber-500 to-orange-600',
    shadowColor: '#F97316',
    moduleOrderRange: [11, 16],
  },
];

/**
 * Map a module order to a theme.
 * Fallback: if order > 16, assign to sector (last theme).
 */
function getThemeForOrder(order) {
  return (
    THEMES.find((th) => order >= th.moduleOrderRange[0] && order <= th.moduleOrderRange[1]) ||
    THEMES[THEMES.length - 1]
  );
}

/**
 * Derive theme status from its modules:
 *  - all completed → 'completed'
 *  - any in_progress or (unlocked but not_started) → 'active'
 *  - none unlocked → 'locked'
 */
function deriveThemeStatus(modules) {
  if (modules.length === 0) return 'locked';
  const allCompleted = modules.every((m) => m.status === 'completed');
  if (allCompleted) return 'completed';
  const hasActive = modules.some(
    (m) => m.status === 'in_progress' || (m.is_unlocked && m.status !== 'completed')
  );
  if (hasActive) return 'active';
  return 'locked';
}

/**
 * Adapts flat backend roadmap array to theme-based stages.
 * Backend: [{module_id, slug, title, order, status, is_unlocked, unlocks_at, percent}]
 * Output: { stages: [themeStage], overallProgress, totalModules, completedModules }
 */
function adaptRoadmapToThemes(arr) {
  // Build theme buckets
  const themeMap = {};
  THEMES.forEach((th) => {
    themeMap[th.id] = { ...th, modules: [] };
  });

  arr.forEach((m) => {
    const moduleOrder = m.order ?? 99;
    const theme = getThemeForOrder(moduleOrder);
    themeMap[theme.id].modules.push({
      id: m.module_id || m.slug,
      module_id: m.module_id,
      slug: m.slug,
      title: m.title,
      order: moduleOrder,
      status: m.status || 'not_started',
      progress: Math.round(m.percent ?? 0),
      is_unlocked: m.is_unlocked ?? false,
      unlocks_at: m.unlocks_at,
      emoji: '📚',
      gradient: getThemeForOrder(moduleOrder).gradient,
    });
  });

  // Sort modules within each theme by order
  Object.values(themeMap).forEach((th) => {
    th.modules.sort((a, b) => a.order - b.order);
  });

  // Build final stages array
  const stages = THEMES.map((th) => {
    const themeModules = themeMap[th.id].modules;
    const completedCount = themeModules.filter((m) => m.status === 'completed').length;
    const totalCount = themeModules.length;
    const progress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
    const status = deriveThemeStatus(themeModules);

    return {
      id: th.id,
      order: th.order,
      name_tr: th.name_tr,
      name_en: th.name_en,
      sub_tr: th.sub_tr,
      sub_en: th.sub_en,
      theme_icon: th.theme_icon,
      gradient: th.gradient,
      shadowColor: th.shadowColor,
      moduleOrderRange: th.moduleOrderRange,
      modules: themeModules,
      moduleIds: themeModules.map((m) => m.id),
      progress,
      completedModules: completedCount,
      totalModules: totalCount,
      status,
      // No CEFR levels — these are career-based themes
    };
  });

  // Overall stats
  const totalCompleted = arr.filter((m) => m.status === 'completed').length;
  const overallProgress = arr.length > 0 ? Math.round((totalCompleted / arr.length) * 100) : 0;

  return {
    stages,
    overallProgress,
    totalModules: arr.length,
    completedModules: totalCompleted,
  };
}

async function fetchCabinRoadmap(lang = 'tr') {
  const token = getToken();
  const res = await axios.get(`${API}/cabin/roadmap?lang=${lang}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (Array.isArray(res.data)) {
    return adaptRoadmapToThemes(res.data);
  }
  if (res.data?.stages) {
    // Already shaped — still adapt if legacy CEFR
    return res.data;
  }
  return adaptRoadmapToThemes(res.data?.items ?? []);
}

export function useCabinRoadmap() {
  const { lang } = useLanguage();
  return useQuery({
    queryKey: ['cabin', 'roadmap', lang],
    queryFn: () => fetchCabinRoadmap(lang),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: typeof window !== 'undefined' && !!getToken(),
  });
}

export { THEMES };
